import { inject, Injectable, signal } from "@angular/core";
import {
  AccountMarketplace,
  AccountType,
  Brand,
  BrandAsset,
  SbCreative,
  SBStrategy,
  SbStrategyCreateRequest,
  StorePage,
  StrategyStateEnum,
} from "@front/m19-api-client";
import { sbAsinsToList, SbStrategyEx } from "@front/m19-models";
import {
  AccountSelectionService,
  BrandAssetsService,
  SbStrategyService,
  TacosStrategiesService,
} from "@front/m19-services";
import { combineLatest, filter, finalize, map, Observable, of, switchMap, tap } from "rxjs";
import { buildSbCreative } from "./sb-build-creative";
import { SbWizardForm } from "./sb-wizard-form";

export type SbFormContextAssets = {
  store?: StorePage;
  brandLogo?: BrandAsset;
  video?: BrandAsset;
};

export interface SbFormContext {
  strategy: SbStrategyEx;
  brand: Brand | null;
}

export interface SbFormContextWithCreative extends SbFormContext {
  creative: SbCreative;
  assets: SbFormContextAssets;
}

export interface SbFormContextArgs {
  strategyId: number;
  fromTacos?: boolean;
}

export interface SbFormContextArgsWithCreative extends SbFormContextArgs {
  creativeId: number;
}

@Injectable()
export class SbWizardCreationService {
  private readonly sbStrategyService = inject(SbStrategyService);
  private readonly tacosStrategiesService = inject(TacosStrategiesService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly brandAssetsService = inject(BrandAssetsService);

  readonly isSubmitting = signal<boolean>(false);

  createStrategyWithCreative(form: SbWizardForm, isVendor: boolean): Observable<SbCreative> {
    return this.strategyWithCreative(form, isVendor).pipe(finalize(() => this.isSubmitting.set(false)));
  }

  createTacosSbStrategy(
    form: SbWizardForm,
    accountMarketplace: AccountMarketplace,
    tacosStrategyGroupId: number,
  ): Observable<SbCreative> {
    return this.strategyWithCreative(form, accountMarketplace.accountType === AccountType.VENDOR).pipe(
      switchMap((creative) =>
        this.tacosStrategiesService
          .updateTacosStrategyGroup({
            accountId: accountMarketplace.accountId,
            marketplace: accountMarketplace.marketplace,
            tacosStrategyGroupId,
            sbStrategyId: creative.strategyId,
          })
          .pipe(map(() => creative)),
      ),
      finalize(() => this.isSubmitting.set(false)),
    );
  }

  createCreativeWithAsins(form: SbWizardForm, strategy: SBStrategy): Observable<SbCreative> {
    return this.creativeWithAsinsCreator(form)(strategy).pipe(finalize(() => this.isSubmitting.set(false)));
  }

  loadContext(args: SbFormContextArgsWithCreative): Observable<SbFormContextWithCreative>;
  loadContext(args: SbFormContextArgs): Observable<SbFormContext>;
  loadContext(
    args: SbFormContextArgs | SbFormContextArgsWithCreative,
  ): Observable<SbFormContext | SbFormContextWithCreative | undefined> {
    const strategy$: Observable<SbStrategyEx | undefined> =
      this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
        switchMap((am) =>
          args.fromTacos
            ? this.tacosStrategiesService
                .getTacosStrategies(am.accountId, am.marketplace, args.strategyId)
                .pipe(map((tacosStrategy) => tacosStrategy.sbStrategy))
            : this.sbStrategyService
                .getStrategies(am.accountId, am.marketplace)
                .pipe(map((strategies) => strategies.get(args.strategyId))),
        ),
      );

    if ("creativeId" in args) {
      return strategy$.pipe(
        switchMap((strategy) => (strategy ? this.withCreativeContext(strategy, args.creativeId) : of(undefined))),
      );
    }

    return strategy$.pipe(switchMap((strategy) => (strategy ? this.withBrandContext(strategy) : of(undefined))));
  }

  private withCreativeContext(strategy: SbStrategyEx, creativeId: number): Observable<SbFormContextWithCreative> {
    return this.brandAssetsService.getSbCreativesPerStrategy(strategy.accountId, strategy.marketplace).pipe(
      map((creatives) => creatives.get(strategy.strategyId)?.find((c) => c.creativeId === creativeId)),
      filter((creative) => creative !== undefined),
      switchMap((creative) => this.withAssetsContext(strategy, creative)),
      switchMap((partial) => this.withBrandAndCreativeContext(partial)),
    );
  }

  private withAssetsContext(
    strategy: SbStrategyEx,
    creative: SbCreative,
  ): Observable<Omit<SbFormContextWithCreative, "brand">> {
    return combineLatest([
      this.brandAssetsService.getBrandAssets(strategy.accountId, strategy.marketplace),
      this.brandAssetsService.storePages$,
    ]).pipe(
      map(([assets, storePages]) => ({
        strategy,
        creative,
        assets: {
          store: storePages.find((p) => p.storePageId === creative.storePageId),
          brandLogo: assets.find((a) => a.assetId === creative.logoAssetId),
          video: assets.find((a) => a.assetId === creative.videoAssetId),
        },
      })),
    );
  }

  private withBrandAndCreativeContext(
    partial: Omit<SbFormContextWithCreative, "brand">,
  ): Observable<SbFormContextWithCreative> {
    return this.sbStrategyService.getBrands(partial.strategy.accountId, partial.strategy.marketplace).pipe(
      map((brands) => ({
        ...partial,
        brand: brands.find((b) => b.brandEntityId === partial.strategy.brandEntityId) ?? null,
      })),
    );
  }

  private withBrandContext(strategy: SbStrategyEx): Observable<SbFormContext> {
    return this.sbStrategyService.getBrands(strategy.accountId, strategy.marketplace).pipe(
      map((brands) => ({
        strategy,
        brand: brands.find((b) => b.brandEntityId === strategy.brandEntityId) ?? null,
      })),
    );
  }

  private strategyWithCreative(form: SbWizardForm, isVendor: boolean): Observable<SbCreative> {
    const setupBrand = form.setup.brand().value();
    const algo = form.setup.algo().value()!;
    const toCreate: SbStrategyCreateRequest = {
      state: StrategyStateEnum.ENABLED,
      brandEntityId: isVendor ? undefined : setupBrand!.brandEntityId,
      name: form.setup.name().value(),
      algoMode: algo.algoMode!,
      acosTarget: algo.acosTarget ? +algo.acosTarget / 100 : undefined,
      dailyBudget: algo.dailyBudget,
      monthlyBudget: algo.monthlyBudget,
      suggestedBid: algo.suggestedBid,
    };
    const createCreative = this.creativeWithAsinsCreator(form);

    this.isSubmitting.set(true);
    return this.sbStrategyService.createStrategy(toCreate).pipe(switchMap(createCreative));
  }

  private creativeWithAsinsCreator(form: SbWizardForm): (strategy: SBStrategy) => Observable<SbCreative> {
    const asinCluster = form.creativesSbAsins
      .sbAsins()
      .value()
      .filter((group) => sbAsinsToList(group).length > 0);
    const assets = form.assets().value();
    const format = form.format().value();
    const setupBrand = form.setup.brand().value();

    return (strategy) => {
      this.isSubmitting.set(true);
      return this.sbStrategyService.createSbCreative(buildSbCreative({ strategy, assets, format, setupBrand })).pipe(
        tap(() => this.isSubmitting.set(true)),
        switchMap((creative: SbCreative) =>
          this.sbStrategyService.updateSbCreativeClusterAsync(creative, asinCluster).pipe(map(() => creative)),
        ),
      );
    };
  }
}
